import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LoadError, errMessage } from "@/components/LoadError";
import { BezoekRij } from "@/components/bezoeken/BezoekRij";
import { NieuwBezoek } from "@/components/bezoeken/NieuwBezoek";
import { magBezoekVastleggen } from "@/components/bezoeken/bezoekUtils";

const seg = (on) => `min-h-[44px] px-3 rounded-sm text-sm font-semibold transition-colors ${on ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100"}`;

export default function Bezoeken() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const bl = user.role === "bedrijfsleider";
  const omId = magBezoekVastleggen(user);
  const [meta, setMeta] = useState(null);
  const [wie, setWie] = useState(user.role === "om" ? "mijn" : "iedereen");
  const [loc, setLoc] = useState("");
  const [lijst, setLijst] = useState(null);
  const [fout, setFout] = useState("");
  const [nieuw, setNieuw] = useState(false);

  const [metaFout, setMetaFout] = useState("");
  const laadMeta = useCallback(() => {
    if (bl) return;
    setMetaFout("");
    api.get("/meta").then((r) => setMeta(r.data)).catch((e) => setMetaFout(errMessage(e)));
  }, [bl]);
  useEffect(() => { laadMeta(); }, [laadMeta]);
  const verzoek = useRef(0);
  useEffect(() => { if (params.get("nieuw") !== null && omId) setNieuw(true); }, [params, omId]);
  const laad = useCallback(() => {
    const q = bl ? {} : { ...(wie === "mijn" ? { om: user.username } : {}), ...(wie === "verwijderd" ? { verwijderd: "1" } : {}), ...(loc ? { locatie: loc } : {}) };
    const nr = ++verzoek.current;   // R1 B7: een trager, ouder antwoord overschrijft de lijst niet
    api.get("/bezoeken", { params: q }).then((r) => { if (nr === verzoek.current) { setLijst(r.data.bezoeken); setFout(""); } })
      .catch((e) => { if (nr === verzoek.current) setFout(errMessage(e)); });
  }, [bl, wie, loc, user.username]);
  useEffect(() => { laad(); }, [laad]);

  const terug = async (b) => {
    try { await api.post(`/bezoeken/${b.id}/terugzetten`); toast.success("Bezoek teruggezet"); laad(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Terugzetten mislukt"); }
  };
  const locs = (meta?.locations || []).filter((l) => !(meta?.generalBuckets || []).includes(l));
  const sluitNieuw = (o) => { setNieuw(o); if (!o && params.get("nieuw") !== null) { params.delete("nieuw"); setParams(params, { replace: true }); } };

  return (
    <div data-testid="bezoeken-page" className="max-w-3xl space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <MapPin className="h-6 w-6 text-blue-600" />
        <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-900">Bezoeken</h1>
        {omId && (
          <button type="button" data-testid="bezoek-vastleggen" onClick={() => setNieuw(true)} disabled={!meta}
            className="ml-auto min-h-[44px] inline-flex items-center gap-2 px-4 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors">
            <Plus className="h-4 w-4" />Bezoek vastleggen
          </button>
        )}
      </div>
      {metaFout && <LoadError error={metaFout} onRetry={laadMeta} testid="bezoeken-meta-error" />}
      {bl && <p data-testid="bezoeken-intro" className="text-sm text-zinc-500">Hier zie je wat je OM bij een bezoek aan je locatie heeft beoordeeld.</p>}
      {!bl && (
        <div className="flex flex-wrap items-center gap-2">
          <div data-testid="bezoeken-filter-wie" className="flex gap-0.5 rounded-md border border-zinc-300 bg-white p-0.5">
            {[["mijn", "Mijn bezoeken"], ["iedereen", "Iedereen"], ...(user.role === "oversight" ? [["verwijderd", "Verwijderd"]] : [])].map(([k, l]) => (
              <button key={k} type="button" data-testid={`bezoeken-filter-${k}`} onClick={() => setWie(k)} className={seg(wie === k)}>{l}</button>
            ))}
          </div>
          <select data-testid="bezoeken-filter-locatie" aria-label="Locatie" value={loc} onChange={(e) => setLoc(e.target.value)}
            className="min-h-[44px] px-3 rounded-md border border-zinc-300 bg-white text-base">
            <option value="">Alle locaties</option>
            {locs.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
      )}
      {fout ? <LoadError error={fout} onRetry={laad} testid="bezoeken-load-error" />
        : !lijst ? <p className="text-sm text-zinc-400">Laden…</p>
        : lijst.length === 0 ? <p data-testid="bezoeken-leeg" className="text-sm text-zinc-500">Nog geen bezoek vastgelegd.</p>
        : (
          <div data-testid="bezoeken-lijst" className="bg-white border border-zinc-200 rounded-[10px] divide-y divide-zinc-200">
            {lijst.map((b) => (
              <BezoekRij key={b.id} b={b} zonderLocatie={bl} onOpen={(x) => (wie === "verwijderd" ? null : navigate(`/bezoeken/${x.id}`))}
                actie={wie === "verwijderd" && <button type="button" data-testid={`bezoek-terugzetten-${b.id}`} onClick={() => terug(b)} className="mr-2 min-h-[44px] px-3 rounded-md border border-zinc-300 text-sm font-semibold hover:bg-zinc-50">Terugzetten</button>} />
            ))}
          </div>
        )}
      {omId && <NieuwBezoek open={nieuw} onOpenChange={sluitNieuw} meta={meta} startLocatie={params.get("nieuw") || ""} />}
    </div>
  );
}
